import {
  AfterViewInit,
  Component,
  Input,
  model,
  OnDestroy,
  OnInit,
  OutputRefSubscription,
  ViewChild
} from '@angular/core';

import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCell,
  MatFooterCellDef,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatNoDataRow,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import {UpdateUserPasswordModel, UserModelView} from '../../../../shared/models/User';
import {UserManagementService} from '../../../../data/users/user-management.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {
  BehaviorSubject,
  combineLatest,
  debounce,
  firstValueFrom,
  mergeMap,
  of,
  Subject,
  Subscription,
  switchMap
} from 'rxjs';
import {MatSort, MatSortHeader, Sort} from '@angular/material/sort';
import {AddUserDialogComponent} from './dialogs/add-user-dialog/add-user-dialog.component';
import {
  ImportUsersSpreadsheetDialogComponent
} from './dialogs/import-users-spreadsheet-dialog/import-users-spreadsheet-dialog.component';
import {
  ImportUsersCartegraphDialogComponent
} from './dialogs/import-users-cartegraph-dialog/import-users-cartegraph-dialog.component';
import {CardComponent} from '../../../../shared/components/card/card.component';
import {CardContentDirective, CardContentRawDirective} from '../../../../shared/components/card/card-content.directive';
import {MatButton, MatIconButton} from '@angular/material/button';
import {AsyncPipe, NgIf} from '@angular/common';
import {MatIcon} from '@angular/material/icon';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {DialogConfirmDeleteUserComponent} from './dialogs/dialog-confirm-delete-user.component';
import {DialogEditUserComponent} from './dialogs/dialog-edit-user.component';
import {DialogChangePasswordComponent} from './dialogs/dialog-change-password.component';
import {PromoteUserComponent} from './dialogs/promote-user/promote-user.component';
import {UserEditMode} from './edit-user/edit-user.component';
import {MatDivider} from '@angular/material/divider';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatFormField, MatPrefix, MatSuffix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatTooltip} from '@angular/material/tooltip';
import {ElementVisibleDirective} from '../../../../shared/directives/element-visible.directive';
import {debounceTime, delay, map, tap} from 'rxjs/operators';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {ConfigurationService} from '../../../../configuration/configuration.service';

@Component({
  selector: 'app-manage-users',
  templateUrl: './manage-users.component.html',
  styleUrls: ['./manage-users.component.scss', '../../settings-tables.scss', '../../settings-common.scss'],
  imports: [
    CardComponent,
    CardContentDirective,
    MatButton,
    NgIf,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatHeaderCellDef,
    MatCell,
    MatIcon,
    MatMenuItem,
    MatMenu,
    MatFooterCell,
    MatHeaderRow,
    MatRow,
    MatFooterRow,
    MatProgressSpinner,
    MatMenuTrigger,
    MatIconButton,
    MatHeaderRowDef,
    MatRowDef,
    MatFooterRowDef,
    MatNoDataRow,
    MatFooterCellDef,
    CardContentRawDirective,
    MatDivider,
    FormsModule,
    MatFormField,
    MatInput,
    MatPrefix,
    MatSuffix,
    MatIconButton,
    AsyncPipe,
    MatTooltip,
    ElementVisibleDirective,
    ReactiveFormsModule,
    MatSlideToggle,
  ]
})
export class ManageUsersComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() origin: any;
  @Input() cartegraphEnabled = false;
  @ViewChild(MatSort) sort: MatSort;
  isInitialized = false;
  isLoading = true;
  uiError: string;
  dataSource: MatTableDataSource<UserModelView> = new MatTableDataSource<UserModelView>([]);
  displayedColumns = [
    'firstName',
    'lastName',
    'login',
    'roles',
    'actions',
  ];

  searchFilter$ = new BehaviorSubject('');
  sort$ = new BehaviorSubject({} as Sort);
  pageToLoad$ = new BehaviorSubject(0);
  guestUsersEnabled: boolean;

  pageSize = 20;

  protected endOfTheUserListVisible = model(false);
  private loadedRecordsCount = 0;
  private totalElements = 0;

  private readonly subscriptions = Array<Subscription | OutputRefSubscription>();

  constructor(
    private readonly userManagementService: UserManagementService,
    private readonly configurationService: ConfigurationService,
    public dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
  ) {
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.endOfTheUserListVisible.subscribe(visible => {
        if (visible && !this.isLoading && this.loadedRecordsCount < this.totalElements) {
          this.pageToLoad$.next(this.pageToLoad$.value + 1);
        }
      })
    );

    this.configurationService.getGuestDriversSupport().then(response => {
      this.guestUsersEnabled = response.data;
    }).catch(error => {
      console.error(error);
    });

    this.isInitialized = true;
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.sort$.next(this.sort);
    this.wireDataLoading();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  deleteUser(email: string) {
    const that = this;
    const user = this.findUser(email);
    const dialogRef = this.dialog.open(DialogConfirmDeleteUserComponent, {
      width: '450px',
      data: {
        email: user.email,
        familyName: user.familyName,
        givenName: user.givenName,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined && result) {
        that.removeUser(email);
        this.showSnackBar('User has been deleted.');
      }
    });
  }

  updateUser(email: string) {
    // reference only
    const user = this.findUser(email);

    // make deep copy
    const userCopy = JSON.parse(JSON.stringify(this.findUser(email)));
    const dialogRef = this.dialog.open(DialogEditUserComponent, {
      width: '450px',
      data: {
        mode: UserEditMode.UPDATE,
        user: {
          email: userCopy.email,
          familyName: userCopy.familyName,
          givenName: userCopy.givenName,
          roles: userCopy.roles,
          phoneNumber: userCopy.phoneNumber,
        } as UserModelView
      },
    });

    firstValueFrom(dialogRef.afterClosed()).then((result) => {
      if (result) {
        Object.assign(user, UserModelView.preload(result, this.cartegraphEnabled));
        this.showSnackBar('User has been updated.');
        this.uiError = '';
      }
    });
  }

  promoteUser(email: string) {
    // reference only
    const user = this.findUser(email);

    // make deep copy
    const userCopy = JSON.parse(JSON.stringify(this.findUser(email)));
    const dialogRef = this.dialog.open(PromoteUserComponent, {
      width: '450px',
      data: {
        email,
        user: {
          email: userCopy.email,
          familyName: userCopy.familyName,
          givenName: userCopy.givenName,
          roles: userCopy.roles,
          phoneNumber: userCopy.phoneNumber,
        } as UserModelView
      },
    });

    firstValueFrom(dialogRef.afterClosed()).then((result) => {
      if (result) {
        Object.assign(user, UserModelView.preload(result, this.cartegraphEnabled));
        this.showSnackBar('User has been updated. Temporary password has been sent.', 5000);
        this.uiError = '';
      }
    });
  }

  changePassword(email: string) {
    const user = this.findUser(email);
    const dialogRef = this.dialog.open(DialogChangePasswordComponent, {
      width: '450px',
      data: {
        email: user.email,
        familyName: user.familyName,
        givenName: user.givenName,
      },
      // panelClass: 'use-material-design-3-theme',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined && result) {
        this.userManagementService
          .updatePassword(new UpdateUserPasswordModel(user.email, result))
          .then((_) => {
            this.showSnackBar('Password has been updated for the user.');
            this.uiError = '';
          })
          .catch((error) => {
            this.uiError = error;
            this.showSnackBar(error);
          });
      }
    });
  }

  showAddDialog(): void {
    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      minWidth: '500px',
      closeOnNavigation: true,
    });

    firstValueFrom(dialogRef.afterClosed()).then((result) => {
      if (result) {
        this.showSnackBar('User has been added.');
        this.handleNewUserFormSubmission();
      }
    });
  }

  showImportSpreadsheetDialog(): void {
    const dialogRef = this.dialog.open(ImportUsersSpreadsheetDialogComponent, {
      minWidth: '500px',
      closeOnNavigation: true,
    });

    firstValueFrom(dialogRef.afterClosed()).then((result) => {
      if (result) {
        this.handleUserImportSucceeded();
      }
    });
  }

  showImportCartegraphDialog() {
    const dialogRef = this.dialog.open(ImportUsersCartegraphDialogComponent, {
      minWidth: '500px',
      closeOnNavigation: true,
    });

    firstValueFrom(dialogRef.afterClosed()).then((result) => {
      if (result) {
        this.handleUserImportSucceeded();
      }
    });
  }

  toggleGuestDriversSupport() {
    this.configurationService.updateGuestDriversSupport(
      this.guestUsersEnabled,
    ).then(() => {
      this.showSnackBar('Guest Drivers Support updated.');
    }).catch((error) => {
      this.showSnackBar('Could not update Guest Drivers Support!', 5000);
    });
  }

  showSnackBar(message: string, duration = 2000) {
    this.snackBar.open(message, null, {duration});
  }

  onSearchKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.searchFilter$.next('');
    }
  }

  private handleNewUserFormSubmission() {
    this.uiError = '';
    this.refreshUsersList();
  }

  private handleUserImportSucceeded() {
    this.uiError = '';
    this.refreshUsersList();
  }

  private refreshUsersList() {
    this.pageToLoad$.next(0);
  }

  private findUser(email: string): UserModelView | null {
    return this.dataSource.data.find(record => record.email === email);
  }

  private removeUser(email: string): void {
    this.dataSource.data = this.dataSource.data.filter(user => user.email !== email);
    this.loadedRecordsCount--;
    this.totalElements--;
  }

  private wireDataLoading() {
    const endOfTheListVisibleCheck = new Subject<void>();

    this.subscriptions.push(endOfTheListVisibleCheck
      .pipe(debounceTime(100))
      .subscribe(() => {
        if (this.endOfTheUserListVisible() && this.loadedRecordsCount < this.totalElements) {
          this.pageToLoad$.next(this.pageToLoad$.value + 1);
        }
      }));

    this.subscriptions.push(
      combineLatest({
          searchFilter: this.searchFilter$.pipe(
            debounce(value => of(true).pipe(delay(value ? 250 : 0)))
          ),
          sort: this.sort$,
        }
      ).pipe(
        tap(() => this.pageToLoad$.next(0)),
        switchMap(({searchFilter, sort}) =>
          this.pageToLoad$.pipe(
            tap(() => {
              this.isLoading = true;
            }),
            mergeMap(pageToLoad =>
              this.userManagementService
                .getUsers(pageToLoad,
                  this.pageSize,
                  this.toSort(sort),
                  null,
                  null,
                  searchFilter,
                  true,
                )
                .pipe(
                  map(result => result.data)
                )
            )
          )
        ),
      ).subscribe(result => {
        this.totalElements = result.totalElements;
        const data = result.content
          .filter(user => !user.email.startsWith('neotreks_support')) // do now show support user
          .map<UserModelView>(user => UserModelView.preload(user, this.cartegraphEnabled));

        if (!result.first) {
          this.dataSource.data = [...this.dataSource.data, ...data];
          this.loadedRecordsCount = result.pageable.offset + result.size;
        } else {
          this.dataSource.data = data;
          this.loadedRecordsCount = data.length;
        }

        this.isLoading = false;

        endOfTheListVisibleCheck.next();
      })
    );
  }

  private toSort(sort: Sort): string {
    return sort ? `${sort.active},${sort.direction}` : '';
  }
}
